// Core
import { useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";

// Components
import { Button, FieldSelect, FieldText, SectionCard } from "components/AtomsV2";
import { Icon } from "components/ShellV2";

// Utils
import { isValidNif, isValidPhone } from "utils/validations";
import { CommunityOrAggregatorTypes } from "utils/constants/index";

function BasicInfo({ data, onUpdate, isLoading }) {
  const [original, setOriginal] = useState(data);

  const [name, setName] = useState(data && data.name);
  const [identifier, setIdentifier] = useState(data && data.identifier);
  const [phone, setPhone] = useState(data && data.phone);
  const [address, setAddress] = useState(data && data.address);
  const [nif, setNif] = useState(data && data.nif);
  const [communityOrAggregatorType, setCommunityOrAggregatorType] = useState(
    data && data.community_or_aggregator_type ? data.community_or_aggregator_type : "EGAC"
  );

  const { t } = useTranslation(["community"]);

  const canSave =
    !isLoading &&
    !(
      original.name === name &&
      original.identifier === identifier &&
      original.phone === phone &&
      original.address === address &&
      original.nif === nif &&
      (original.community_or_aggregator_type ?? "EGAC") === communityOrAggregatorType
    ) &&
    isValidPhone(phone) &&
    isValidNif(nif);

  const onUpdateClicked = async () => {
    if (canSave) {
      const newData = {
        name,
        identifier,
        phone,
        address,
        nif,
        community_or_aggregator_type: communityOrAggregatorType,
      };

      const success = await onUpdate(newData, t("basic_info.updated_ok"));
      if (success) {
        setOriginal(newData);
      }
    }
  };

  return (
    <SectionCard
      id="general"
      icon="badge"
      title={t("basic_info.title")}
      action={
        <Button variant="ghost" size="sm" disabled={!canSave} onClick={onUpdateClicked}>
          <Icon name="save" />
          {t("basic_info.update")}
        </Button>
      }
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <FieldText label={t("basic_info.name")} value={name} onChange={setName} />
        <FieldText label={t("basic_info.identifier")} value={identifier} onChange={setIdentifier} />
        <FieldText
          label={t("basic_info.phone")}
          value={phone}
          onChange={setPhone}
          error={!isValidPhone(phone)}
        />
        <FieldText label={t("basic_info.address")} value={address} onChange={setAddress} />
        <FieldText
          label={t("basic_info.nif")}
          value={nif}
          onChange={setNif}
          error={!isValidNif(nif)}
        />
        <FieldSelect
          label={t("basic_info.community_or_aggregator_type", {
            defaultValue: "Tipo de Comunidade/Agregador",
          })}
          value={communityOrAggregatorType}
          options={CommunityOrAggregatorTypes}
          onChange={setCommunityOrAggregatorType}
        />
      </div>
    </SectionCard>
  );
}

BasicInfo.defaultProps = {
  isLoading: false,
};

BasicInfo.propTypes = {
  data: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    phone: PropTypes.string,
    address: PropTypes.string,
    nif: PropTypes.string,
    methodAllocCoeff: PropTypes.string,
    identifier: PropTypes.string,
    community_or_aggregator_type: PropTypes.string,
  }).isRequired,
  onUpdate: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
};

export default BasicInfo;
